import React from 'react';
import type { FC } from 'react';
import { Trans, useTranslation } from '@wix/cli-i18n';
import { isType, match } from 'variant';
import { DeploymentStatus } from '@wix/document-management-client';
import {
  Alert,
  Link,
  Spinner,
  useAsync,
  useAsyncCallback,
  Box,
} from '@wix/cli-ui-kit';
import { ErrorViewer } from '@wix/cli-error-reporting';
import type { DeploymentSourceTypes } from '../gridapp/deployApp.js';
import { useProjectModel } from '../model.js';
import { SelectPreviewSource } from './SelectPreviewSource.js';
import { useCreatePreview } from './useCreatePreview.js';

interface PreviewCommandOptions {
  source?: DeploymentSourceTypes;
  force?: boolean;
}

export const PreviewCommand: FC<PreviewCommandOptions> = (options) => {
  const { t } = useTranslation();
  const { model } = useProjectModel();
  const createPreview = useCreatePreview();

  const { status, execute: handleCreatingPreview } = useAsyncCallback(
    async (_, previewSource: DeploymentSourceTypes) => {
      const { previewUrl, deploymentStatus } =
        await createPreview(previewSource);

      if (!options.force && isType(deploymentStatus, DeploymentStatus.Error)) {
        throw deploymentStatus.deploymentError;
      }

      return { previewUrl, deploymentStatus };
    }
  );

  // Create preview when source is passed as an option
  useAsync(async () => {
    if (options.source) {
      void handleCreatingPreview(options.source);
    }
  }, []);

  return (
    <>
      {!options.source && (
        <SelectPreviewSource
          projectFolder={model.projectFolder}
          onSubmit={({ value }) => handleCreatingPreview(value.type)}
        />
      )}
      {match(status, {
        NotRequested: () => null,
        Error: () => null,
        Loading: () => <Spinner text={t('preview_command.creating_preview')} />,
        Success: ({ result: { previewUrl, deploymentStatus } }) => (
          <>
            {isType(deploymentStatus, DeploymentStatus.Error) ? (
              <Box marginBottom={1} flexDirection="column">
                <ErrorViewer error={deploymentStatus.deploymentError} />
              </Box>
            ) : null}
            <Alert type="success">
              <Trans
                i18nKey="preview_command.preview_created"
                components={[<Link url={previewUrl} />]}
              />
            </Alert>
          </>
        ),
      })}
    </>
  );
};
